<template>
  <div class="i-confirmation">
    <div class="main">
      <div class="left">
        <div class="content-wrapper">
          <div class="logo">
            <img
              :src="logo"
              loading="lazy"
              alt=""
            />
          </div>
          <div class="inner">
            <template v-if="currentStepKey === StepKey.WELCOME">
              <div class="badge">Weekly Career Coaching</div>
              <div class="header highlighted">Welcome to The JobTest Career Accelerator</div>
              <div class="text welcome">
                <span class="title"><strong>In addition to your career report you get...</strong></span>
                weekly live coaching sessions led by certified career coaches, focused on helping you get clarity, build
                confidence, and make progress. <br />
                <span class="highlighted"><strong>All included. No extra cost. Just show up.</strong></span>
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.CALENDAR">
              <div class="header">Make sure it’s on your calendar.</div>
              <div class="text">Click the button below to add an event reminder to your calendar.</div>
              <iBaseButton
                v-if="data.groupCoachingLinks.googleCalendarUrl"
                v-bind="otherButtonStyle"
                text="Add to Google Calendar"
                @clicked="openGoogleCalendar"
              />
              <div class="list">
                <a
                  v-if="data.groupCoachingLinks.webcalUrl"
                  class="link"
                  :href="data.groupCoachingLinks.webcalUrl"
                  target="_blank"
                  >Apple Calendar</a
                >
                <a
                  v-if="data.groupCoachingLinks.outlookCalendarUrl"
                  class="link"
                  :href="data.groupCoachingLinks.outlookCalendarUrl"
                  target="_blank"
                  >Outlook Calendar</a
                >
                <a
                  v-if="data.groupCoachingLinks.httpsUrl"
                  class="link"
                  :href="data.groupCoachingLinks.httpsUrl"
                  target="_blank"
                  >Others</a
                >
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.WHAT_YOU_GET">
              <div class="badge">What You Get</div>
              <div class="header highlighted">
                <span class="highlighted">Live Career Coaching.</span><br />
                <span class="highlighted">Every Week.</span>
              </div>
              <div class="text">
                No guesswork, no fluff — just real help from certified coaches who’ve helped thousands of people land
                better jobs, negotiate more, and move faster.
              </div>
              <div class="list">
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Step-by-step support and live Q&A with coaches
                </div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />4 hours of expert coaching each month
                </div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Weekly momentum with practical guidance
                </div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Tactical advice you can actually use — resumes, interviews, salary, and more
                </div>
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.HOW_IT_WORKS">
              <div class="header">How it Works</div>
              <div class="text">
                From start to finish, our tools and expert coaches guide you every step of the way through your job
                search so you can find the job that you love.
              </div>
              <div
                v-if="isMdOrLarger"
                class="cards"
              >
                <div class="shadow-card">
                  <div class="large highlighted">200x</div>
                  <div><strong>Return on Investment</strong></div>
                  <div class="small">
                    Saved weeks mean an extra $20K, plus multiple offers often lead to a $30K salary increase.
                  </div>
                </div>
                <div class="shadow-card">
                  <div class="large highlighted">72%</div>
                  <div><strong>Wish they joined sooner</strong></div>
                  <div class="small">
                    Of the people who didn’t join right away, 72% that were interviewed said they wish they had attended
                    sooner.
                  </div>
                </div>
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.WHAT_MISSING">
              <div class="badge">Let's be honest...</div>
              <div class="header"><span class="highlighted">Some people won't join.</span></div>
              <div class="text">But the ones who do get support, take action, and build momentum twice as fast.</div>
              <div class="list">
                <div class="head">Skip your sessions if you like:</div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Applying to jobs that don't match your skills
                </div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Missing out on salary negotiations
                </div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Struggling with interview preparation
                </div>
                <div class="row">
                  <img
                    class="radio"
                    :src="iconFilledRadio"
                  />Feeling isolated in your job search
                </div>
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.TESTIMONIALS">
              <div class="header">
                “The report was great, but the
                <span class="highlighted">group coaching sessions were how I turned discovery into action”</span>
              </div>
              <div
                v-if="isMdOrLarger"
                class="cards"
              >
                <div class="shadow-card">
                  <div class="large highlighted">81%</div>
                  <div><strong>Landed jobs in 3 months</strong></div>
                  <div class="small">
                    40% from our applications, 41% from personal networking and individual efforts.
                  </div>
                </div>
                <div class="shadow-card">
                  <div class="large highlighted">40%</div>
                  <div><strong>Less time spent in job search</strong></div>
                  <div class="small">We reduce the average 5 months job search to 1-3 months.</div>
                </div>
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.SEE_YOU_THERE">
              <div class="header"><span class="highlighted">See you there?!</span></div>
              <div class="action">
                <iBaseButton
                  v-bind="otherButtonStyle"
                  text="No, I can land a job on my own."
                  pre-icon=""
                  :text-color="amethyst500"
                  text-gradient=""
                  @clicked="goToReadyCard(false)"
                />
                <iBaseButton
                  v-bind="nextButtonStyle"
                  text="Count me in!"
                  :width="isMdOrLarger ? '180px' : '100%'"
                  icon-url=""
                  @clicked="goToReadyCard(true)"
                />
              </div>
            </template>
            <template v-if="currentStepKey === StepKey.READY">
              <div class="badge">Almost There</div>
              <div class="header">Your results are in.</div>
              <div class="header">But, here's the thing....</div>
              <div class="header"><span class="squiggly">a report can only give you clarity.</span></div>
              <div class="text">Here's something that will make it 10x more useful.</div>
            </template>
          </div>
          <div
            v-if="isDesktopOrLarger"
            class="actions"
          >
            <div>
              <iBaseButton
                v-if="data.activeIndex !== 0"
                v-bind="prevButtonStyle"
                @clicked="prevStep"
              />
            </div>
            <iBaseButton
              v-if="currentStepKey != StepKey.SEE_YOU_THERE"
              v-bind="nextButtonStyle"
              @clicked="data.activeIndex < MAX_STEP ? nextStep() : goToCoachingUpsell()"
            />
          </div>
        </div>
      </div>
      <div class="right">
        <template v-if="currentStepKey === StepKey.WELCOME">
          <div
            class="linda-testimonial"
            :style="{
              '--bg-img': 'url(' + imageLinda + ')'
            }"
          >
            <div class="rating">
              <img :src="iconStars1" />4.9 rating
              <span>(<u>1.2k+ reviews</u>)</span>
            </div>
            <div><strong>Last month 600+ Clients</strong> landed their dream Careers with Linda.</div>
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.CALENDAR">
          <div class="calendar-appointment">
            <img
              class="appointment"
              :src="imageAppointment"
            />
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.WHAT_YOU_GET">
          <div class="what-you-get-scroll">
            <img
              class="wyg"
              :src="imageWYG"
            />
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.HOW_IT_WORKS">
          <div class="how-it-works-steps">
            <div class="grad-card">
              <div class="card">
                <div
                  class="grad-card"
                  :style="{ borderRadius: '32px', padding: '2px' }"
                >
                  <div
                    class="card"
                    :style="{ borderRadius: '31px', padding: '5px 14px', flex: '1 0 71px' }"
                  >
                    Step 1
                  </div>
                  <span>Completed</span>
                  <img :src="iconCheck" />
                </div>
                <div>
                  Complete your free and comprehensive career assessment and resume and receive personalized results.
                </div>
              </div>
            </div>
            <div class="grad-card">
              <div class="card">
                <div class="first">
                  <div
                    class="grad-card"
                    :style="{ borderRadius: '32px', padding: '2px' }"
                  >
                    <div
                      class="card"
                      :style="{ borderRadius: '31px', padding: '5px 14px', flex: '1 0 71px' }"
                    >
                      Step 2
                    </div>
                  </div>
                  <div class="highlighted"><strong>Join your weekly coaching cohort</strong></div>
                </div>
                <div>
                  Attend live weekly group coaching sessions and take advantage of proven job search strategies and
                  tips.
                </div>
              </div>
            </div>
            <div class="grad-card">
              <div class="card">
                <div class="first">
                  <div
                    class="grad-card"
                    :style="{ borderRadius: '32px', padding: '2px' }"
                  >
                    <div
                      class="card"
                      :style="{ borderRadius: '31px', padding: '5px 14px', flex: '1 0 71px' }"
                    >
                      Step 3
                    </div>
                  </div>
                  <div class="highlighted"><strong>Get real-time support</strong></div>
                </div>
                <div>
                  Your coach is here every week to answer questions, review your progress, give feedback, and help you
                  stay accountable — so you don't get stuck or go it alone.
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.WHAT_MISSING">
          <div class="time-warning-section">
            <div class="grad-card">
              <div
                class="card"
                :style="{ padding: '22px 24px' }"
              >
                <div class="time">
                  <img :src="iconTime" />
                  <strong>Don't waste more time</strong>
                </div>
                <div>
                  The average job search takes 6+ months. Our members find jobs
                  <span class="highlighted">2x faster</span>.
                </div>
              </div>
            </div>
            <div
              v-if="!isDesktopOrLarger"
              class="mob-jobs"
            >
              <img :src="imageJobBoard" />
            </div>
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.TESTIMONIALS">
          <div class="testimonials-list">
            <div
              v-for="(testimonial, index) in TESTIMONIALS"
              :key="index"
              class="testimonial"
              :style="{ marginLeft: [1, 3].includes(index) ? 'auto' : 'unset' }"
            >
              <div class="top">
                <div class="intro">{{ testimonial.intro }}</div>
                <img
                  class="rating"
                  :src="iconStars1"
                />
              </div>
              <div
                class="comment"
                v-html="testimonial.comment"
              ></div>
              <div class="name">-{{ testimonial.name }}</div>
            </div>
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.SEE_YOU_THERE">
          <div class="see-you-there-card">
            <div class="testimonial">
              <div class="top">
                <div class="intro">{{ TESTIMONIALS[0].intro }}</div>
                <img
                  class="rating"
                  :src="iconStars1"
                />
              </div>
              <div
                class="comment"
                v-html="TESTIMONIALS[0].comment"
              ></div>
              <div class="name">-{{ TESTIMONIALS[0].name }}</div>
            </div>
            <img :src="imageLaptopCoach" />
            <div class="schools">
              <template
                v-for="school in arrayOfSchools"
                :key="school"
              >
                <img
                  class="school"
                  :src="school"
                />
              </template>
            </div>
          </div>
        </template>
        <template v-if="currentStepKey === StepKey.READY">
          <div class="testimonials-stats">
            <!-- <div class="header">
              Your skills + our roadmap <br />
              = proven impact
            </div> -->
            <div class="reviews">
              <div
                v-for="review in [...REVIEWS, ...REVIEWS]"
                :key="review.name"
                class="review"
              >
                <div class="top">
                  <div class="rating"><img :src="iconStars1" /> {{ review.rating }} stars</div>
                  <div
                    class="comment"
                    v-html="review.comment"
                  ></div>
                </div>
                <div class="person">
                  <div class="name">{{ review.name }}</div>
                  <div>{{ review.job }}</div>
                </div>
              </div>
            </div>
            <div class="stats">
              <div class="stat">
                <div class="header highlighted">120K+</div>
                Happy Members
              </div>
              <div class="stat">
                <div class="header highlighted">89%</div>
                Job Success Rate
              </div>
              <div class="stat">
                <div class="header highlighted">2x</div>
                Job Interviews
              </div>
            </div>
          </div>
        </template>
      </div>
      <div
        v-if="!isDesktopOrLarger"
        class="actions"
      >
        <div>
          <iBaseButton
            v-if="data.activeIndex !== 0"
            v-bind="prevButtonStyle"
            @clicked="prevStep"
          />
        </div>
        <iBaseButton
          v-if="currentStepKey != StepKey.SEE_YOU_THERE"
          v-bind="nextButtonStyle"
          @clicked="data.activeIndex < MAX_STEP ? nextStep() : goToCoachingUpsell()"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import router from "@/router"
import { useUserStore } from "@/stores/user"
import { amethyst500, black, black100, bluePurpleGradient, white } from "@/styles/colors"
import { breakpointMd, breakpointLg } from "@/styles/variables"
import { computed, nextTick, onMounted, reactive, watch } from "vue"
import { useRoute } from "vue-router"

import useIsScreenSize from "@/composables/useIsScreenSize"
import { GlobalVariables } from "@/helpers/globalVariables"
import GtmService, { UserFlow } from "@/services/gtmService"
import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import imageJobBoard from "@/assets/confirm/job-boards-mobile.avif"
import imageAppointment from "@/assets/confirm/appointment.avif"
import imageLinda from "@/assets/confirm/linda.avif"
import imageWYG from "@/assets/confirm/what-you-get.avif"
import imageLaptopCoach from "@/assets/confirm/laptop-coach.avif"
import iconArrowRight from "@/assets/icons/icon-arrow-right-white.svg"
import iconCheck from "@/assets/icons/icon-checkmark-white.svg"
import iconFilledRadio from "@/assets/icons/icon-filled-radio.svg"
import iconStars1 from "@/assets/icons/icon-five-slashed-stars.svg"
import iconGoogle from "@/assets/icons/icon-google-color.svg"
import iconTime from "@/assets/icons/icon-time-gradient.svg"
import logo from "@/assets/logo/logo-dark.svg"
import imageSchool4 from "@/assets/welcome/school-chicago.svg"
import imageSchool11 from "@/assets/welcome/school-cornell.svg"
import imageSchool7 from "@/assets/welcome/school-duke.svg"
import imageSchool6 from "@/assets/welcome/school-harvard.svg"
import imageSchool2 from "@/assets/welcome/school-hopkins.svg"
import imageSchool8 from "@/assets/welcome/school-mit.svg"
import imageSchool5 from "@/assets/welcome/school-north.svg"
import imageSchool9 from "@/assets/welcome/school-penn.svg"
import imageSchool12 from "@/assets/welcome/school-princeton.svg"
import imageSchool3 from "@/assets/welcome/school-stanford.svg"
import imageSchool1 from "@/assets/welcome/school-ucla.svg"
import imageSchool10 from "@/assets/welcome/school-yale.svg"

// STORES AND COMPOSABLES
const userStore = useUserStore()
const isDesktopOrLarger = useIsScreenSize(breakpointLg)
const isMdOrLarger = useIsScreenSize(breakpointMd)
const route = useRoute()

// TYPES
enum StepKey {
  WELCOME = "welcome",
  CALENDAR = "calendar",
  WHAT_YOU_GET = "what-you-get",
  HOW_IT_WORKS = "how-it-works",
  WHAT_MISSING = "what-missing",
  TESTIMONIALS = "testimonials",
  SEE_YOU_THERE = "see-you-there",
  READY = "ready"
}

// CONSTANTS
const STEP_KEYS: StepKey[] = [
  StepKey.WELCOME,
  StepKey.WHAT_YOU_GET,
  StepKey.CALENDAR,
  StepKey.HOW_IT_WORKS,
  StepKey.WHAT_MISSING,
  StepKey.TESTIMONIALS,
  StepKey.SEE_YOU_THERE,
  StepKey.READY
]
const CTA_TEXT = ["Continue", "Ready?", "Continue", "Why?", "Continue", "Continue", "Continue", "Show me"]
const MAX_STEP = 7
const CALENDAR_STEP_KEY = StepKey.CALENDAR
const arrayOfSchools = [
  imageSchool1,
  imageSchool2,
  imageSchool3,
  imageSchool4,
  imageSchool5,
  imageSchool6,
  imageSchool7,
  imageSchool8,
  imageSchool9,
  imageSchool10,
  imageSchool11,
  imageSchool12
]
const TESTIMONIALS = [
  {
    name: "Jordan P.",
    intro: "I didn’t think I needed this",
    comment:
      "I spent months applying on my own. These sessions gave me the momentum and tools I didn’t even know I was missing. <span class='highlighty'><strong>Best $30 I’ve spent on my career.</strong></span>"
  },
  {
    name: "Sam M.",
    intro: "I wish I joined sooner...",
    comment:
      "I didn’t realize how weak my resume bullets were. In 20 minutes, they helped me rewrite three lines that finally <span class='highlighty'><strong>got me interviews.</strong></span>"
  },
  {
    name: "Rachel C.",
    intro: "I don’t want coaching",
    comment:
      "I thought I wanted tools, not advice. But watching other people get feedback helped me fix my own resume — I filled a page with notes and <span class='highlighty'><strong>rewrote my resume that same day.</strong></span>"
  },
  {
    name: "Alex L.",
    intro: "Totally didn’t think it’d be worth it",
    comment:
      "I was tired and wasn’t sure it’d be worth it but I was struggling how to position my background. They helped me write one line <span class='highlighty'><strong>that reframed my entire story. It changed how I talk in interviews.</strong></span>"
  }
]
const REVIEWS = [
  {
    name: "Jessica M.",
    rating: 4.9,
    comment:
      "I’ve taken dozens of career quizzes, <span class='highlighty'><strong>but nothing ever felt this tailored.</strong></span> <br /><br />Jobtest didn’t just give me job titles—it gave me clarity. The personality analysis alone was worth it. I finally understand how I’m wired, and <span class='highlighty'>what work will actually energize me.</span>",
    job: "Senior UX Designer"
  },
  {
    name: "David K.",
    rating: 4.8,
    comment:
      "<strong>I was skeptical—thought it’d be generic.</strong> <br /><br />Instead, <span class='highlighty'>it nailed my strengths,</span> explained why I’ve been feeling stuck, and introduced me to <span class='highlighty'>careers I didn’t even know existed.</span> This report didn’t just make suggestions, it gave me a roadmap. 100% worth it.",
    job: "Product Manager"
  },
  {
    name: "Priya S.",
    rating: 5,
    comment:
      "This was like <span class='highlighty'>therapy meets career coaching</span> in one place. I saw myself clearly for the first time in years—and it didn’t just stop there. <br /><br /><span class='highlighty'>The report</span> translated all of that into <span class='highlighty'>actionable career</span> options, with <span class='highlighty'>salary ranges and next steps.</span>",
    job: "People Operations Specialist"
  },
  {
    name: "Lily T.",
    rating: 4.9,
    comment:
      "Honestly, I expected fluff. What I got was a deep, <span class='highlighty'>personalized breakdown</span> of who I am, what I’m good at, and where I’m likely to thrive. <br /><br /><span class='highlighty'>It helped me reframe my whole job search</span>—and I’ve already <span class='highlighty'>booked two interviews</span> using the resume tips.",
    job: "Marketing Strategist"
  },
  {
    name: "Chris D.",
    rating: 5,
    comment:
      "I thought I needed more degrees to figure my life out—but this report helped me realize <span class='highlighty'>I already had everything I needed to start.</span> <br /><br />It laid out career paths that matched my personality, explained next steps, and made it all feel actually <span class='highlighty'>doable.</span>",
    job: "Entry-Level Product Researcher"
  }
]

// DATA
const data = reactive({
  countdown: 10,
  oneSecondInMs: 1000,
  activeIndex: 0,
  groupCoachingLinks: {
    googleCalendarUrl: "",
    outlookCalendarUrl: "",
    httpsUrl: "",
    webcalUrl: ""
  }
})

// COMPUTED
const currentStepKey = computed(() => {
  return STEP_KEYS[data.activeIndex] || ""
})

const hasCalendarLinks = computed(() => {
  return !!(
    data.groupCoachingLinks.googleCalendarUrl ||
    data.groupCoachingLinks.outlookCalendarUrl ||
    data.groupCoachingLinks.httpsUrl ||
    data.groupCoachingLinks.webcalUrl
  )
})

const prevButtonStyle = computed(() => {
  return {
    size: "medium",
    backgroundColor: "transparent",
    borderRadius: "8px",
    fontWeight: "700",
    textColor: black,
    width: "fit-content",
    isHoverable: false,
    text: "Back"
  }
})
const nextButtonStyle = computed(() => {
  return {
    size: "medium",
    fontWeight: "500",
    width: currentStepKey.value === StepKey.WELCOME && !isDesktopOrLarger.value ? "100%" : "180px",
    backgroundColor: amethyst500,
    borderRadius: "8px",
    textColor: white,
    text: CTA_TEXT[data.activeIndex],
    iconUrl: iconArrowRight
  }
})
const otherButtonStyle = computed(() => {
  return {
    version: "outline",
    size: "medium",
    textGradient: bluePurpleGradient,
    backgroundColor: "transparent",
    borderRadius: "8px",
    borderColor: black100,
    fontWeight: "500",
    width: "fit-content",
    padding: "16px 24px",
    isHoverable: false,
    preIcon: iconGoogle
  }
})

// FUNCTION
const scrollToTop = () => {
  window.scrollTo({ top: 0 })
}

const updateUrlStep = (step: number) => {
  const currentQuery = { ...route.query }
  if (step === 0) {
    // Remove step from URL if it's the first step
    delete currentQuery.step
  } else {
    currentQuery.step = step.toString()
  }

  router.replace({
    name: route.name,
    query: currentQuery
  })
}

const setStep = (step: number) => {
  if (step >= 0 && step <= MAX_STEP) {
    data.activeIndex = step
    updateUrlStep(step)
    nextTick(() => {
      scrollToTop()
    })
  }
}

const nextStep = () => {
  let newStep = data.activeIndex + 1

  // Skip calendar step if no calendar links are available
  if (STEP_KEYS[newStep] === CALENDAR_STEP_KEY && !hasCalendarLinks.value) {
    newStep = newStep + 1
  }

  setStep(newStep)
}

const prevStep = () => {
  let newStep = data.activeIndex - 1

  // Skip calendar step if no calendar links are available
  if (STEP_KEYS[newStep] === CALENDAR_STEP_KEY && !hasCalendarLinks.value) {
    newStep = newStep - 1
  }

  setStep(newStep)
}

const goToReadyCard = async (groupCoachingInterest: boolean) => {
  try {
    await Promise.all([
      api.event.postEvent(`groupCoachingInterest${groupCoachingInterest ? "Yes" : "No"}`),
      GtmService.groupCoachingInterestEvent(groupCoachingInterest)
    ])
  } catch (error) {
    console.error(`Error registering user's group coaching interest as ${groupCoachingInterest ? "yes" : "no"}`, error)
  }

  nextStep()
}
const goToCoachingUpsell = async () => {
  await userStore.loadUserData()
  router.push({ name: GlobalVariables.urls.report.children.coachingUpsellB.routeName })
}

const openGoogleCalendar = () => {
  if (data.groupCoachingLinks.googleCalendarUrl) {
    window.open(data.groupCoachingLinks.googleCalendarUrl, "_blank")
  }
}

async function reportOrderToGoogleTagManager() {
  const urlParams = new URLSearchParams(window.location.search)
  const sessionId = urlParams.get("session_id")
  const valueAsString = urlParams.get("val")
  const value = valueAsString ? parseFloat(valueAsString) : 0

  if (!sessionId) return

  try {
    await GtmService.purchaseEvent(UserFlow.REPORT, sessionId, value)
  } catch (error) {
    console.error("Error reporting report purchase to Google Tag Manager", error)
  }
}

// LIFECYCLE HOOKS
onMounted(async () => {
  // Initialize activeIndex from URL step parameter
  const stepParam = route.query.step
  if (stepParam && typeof stepParam === "string") {
    const step = parseInt(stepParam, 10)
    if (!isNaN(step) && step >= 0 && step <= MAX_STEP) {
      data.activeIndex = step
    }
  }

  await reportOrderToGoogleTagManager()

  // Load group coaching links
  const response = userStore.getUserId ? await api.calendar.getGroupCoachingLinks(userStore.getUserId) : null
  if (response) {
    data.groupCoachingLinks = response
  }

  // Skip calendar step if no calendar links are available and user is on calendar step
  if (currentStepKey.value === CALENDAR_STEP_KEY && !hasCalendarLinks.value) {
    setStep(2)
  }

  // Scroll to top when page loads
  nextTick(() => {
    scrollToTop()
  })
})

// Watch for URL changes and update activeIndex accordingly
watch(
  () => route.query.step,
  (newStep) => {
    if (newStep && typeof newStep === "string") {
      const step = parseInt(newStep, 10)
      if (!isNaN(step) && step >= 0 && step <= MAX_STEP && step !== data.activeIndex) {
        // Skip calendar step if no calendar links are available
        if (STEP_KEYS[step] === CALENDAR_STEP_KEY && !hasCalendarLinks.value) {
          // Determine direction based on the difference between new step and current step
          const direction = step > data.activeIndex ? 1 : -1
          setStep(direction === 1 ? 2 : 0)
        } else {
          data.activeIndex = step
          // Scroll to top when URL changes
          nextTick(() => {
            scrollToTop()
          })
        }
      }
    } else if (!newStep && data.activeIndex !== 0) {
      data.activeIndex = 0
      // Scroll to top when returning to first step
      nextTick(() => {
        scrollToTop()
      })
    }
  }
)
</script>

<style scoped lang="scss">
.i-confirmation {
  @include flex(row, center, center);

  min-height: 100vh;
  width: 100%;
  position: relative;

  @media screen and (min-width: $breakpoint-lg) {
    height: 100vh;
    padding: unset;
  }

  .main {
    @include flex(column, start, stretch);

    width: 100%;
    min-height: 100vh;
    overflow-y: auto;
    position: relative;

    @media screen and (min-width: $breakpoint-lg) {
      @include flex(row, center, stretch);

      height: 100vh;
      min-height: unset;
      overflow-y: visible;
    }

    .badge {
      @include flex(row, center, center, $gap: 8px);
      @include text-sm;
      @include border-radius-xxl;

      width: fit-content;
      background-image: $blue-purple-gradient;
      padding: 4px 12px;
      color: $white;

      @media screen and (min-width: $breakpoint-md) {
        font-weight: 500;
        padding: 8px 16px;
      }
    }

    .highlighted {
      background-image: $blue-purple-gradient;
      color: transparent;
      background-clip: text;
    }

    .header {
      @include text-xl;

      font-weight: 700;

      @media screen and (min-width: $breakpoint-sm) {
        @include display-xs;
      }

      @media screen and (min-width: $breakpoint-md) {
        @include display-md;
      }

      > .squiggly {
        color: var(--hero-accent);
        position: relative;

        &::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 6px;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 8' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q10 1 20 5 Q30 9 40 5 Q50 1 60 5 Q70 9 80 5 Q90 1 100 5 Q110 9 120 5' fill='none' stroke='%235B4CFF' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")
            repeat-x;
          background-size: 60px 6px;
          opacity: 0.45;
        }
      }
    }

    .text {
      @include text-lg;

      max-width: 500px;
    }

    .action {
      @include flex(column-reverse, center, center, $gap: 20px);

      @media screen and (min-width: $breakpoint-md) {
        @include flex(row, start, stretch, $gap: 20px);

        margin-top: 60px;
      }
    }

    .link {
      color: $purple-heart900;
      font-size: 18px;
      font-weight: 500;
      text-decoration: underline;
      line-height: 18px;
      word-wrap: break-word;
    }

    .grad-card {
      @include flex(row, start, center, $gap: 12px);
      @include border-radius-md;

      background-image: $blue-purple-gradient;
      padding: 1px;
      width: fit-content;
      max-width: 520px;
      color: $white;

      @media screen and (min-width: $breakpoint-lg) {
        max-width: 420px;
      }

      > img {
        margin-right: 14px;
      }

      .card {
        @include flex(column, start, stretch, $gap: 12px);

        background-color: white;
        border-radius: 11px;
        padding: 12px 24px;
        color: $black;

        .first {
          @include flex(row, start, center, $gap: 12px);
        }
      }
    }

    .cards {
      @include flex(row, center, stretch, $gap: 16px);

      .shadow-card {
        @include flex(column, start, start, $gap: 8px);

        background-color: white;
        border-radius: 11px;
        padding: 20px;
        color: $black;
        box-shadow: 0 14px 18px 0 #0000001b;
        width: 50%;
        text-align: start;
        margin-top: 10px;

        .large {
          @include display-lg;

          font-weight: 700;
        }

        .small {
          @include text-sm;
        }
      }
    }

    .testimonial {
      @include flex(column, start, stretch, $gap: 10px);
      @include border-radius-md;
      @include text-sm;

      text-align: start;
      background-color: $white;
      padding: 20px;
      width: 100%;
      box-shadow: 0 14px 18px 0 #122b6905;

      @media screen and (min-width: $breakpoint-md) {
        width: 90%;
      }

      @media screen and (min-width: $breakpoint-xl) {
        width: 80%;
      }

      .top {
        @include flex(row, space-between, start, $gap: 10px);

        @media screen and (min-width: $breakpoint-sm) {
          align-items: center;
        }

        .intro {
          @include text-sm;

          font-weight: 700;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-md;
          }

          @media screen and (min-width: $breakpoint-md) {
            @include text-lg;
          }
        }

        .rating {
          width: 90px;
        }
      }

      .name {
        font-weight: 700;
        margin-left: auto;
      }
    }

    .left {
      @include flex(column, start, center, $gap: 20px);

      padding: 20px; // Add top padding to prevent cutoff
      background-color: $white;
      min-height: auto;

      @media screen and (min-width: $breakpoint-sm) {
        gap: 35px;
        padding: 25px 30px 30px; // Increase top padding on tablet
      }

      @media screen and (min-width: $breakpoint-lg) {
        align-items: stretch;
        padding: 30px;
        gap: 0;
        width: 50%;
        flex: 0 0 50%;
        min-height: 100vh;
        overflow-y: visible;
      }

      .content-wrapper {
        @include flex(column, start, center, $gap: 20px);

        width: 100%;

        @media screen and (min-width: $breakpoint-sm) {
          gap: 35px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          align-items: stretch;
          max-width: 615px;
          margin: 0 auto;
          gap: 0;
          height: 100%;
          justify-content: space-between;
        }
      }

      .logo {
        width: 120px;
        height: 29px;
      }

      .inner {
        @include flex(column, center, center, $gap: 15px);

        text-align: center;
        width: 100%;
        flex: 1;

        @media screen and (min-width: $breakpoint-lg) {
          align-items: stretch;
          text-align: start;
          justify-content: center;
        }

        .list {
          @include flex(column, start, stretch, $gap: 15px);

          margin-top: 30px;
          text-align: start;

          .head {
            @include text-xl;

            font-weight: 600;
            text-align: center;

            @media screen and (min-width: $breakpoint-lg) {
              text-align: start;
            }
          }

          .row {
            @include flex(row, start, stretch, $gap: 8px);

            .radio {
              width: 20px;
              flex: 0 0 20px;
            }
          }
        }

        .welcome {
          @include flex(column, start, center, $gap: 5px);

          @media screen and (min-width: $breakpoint-lg) {
            @include flex(column, start, start, $gap: 12px);
          }

          .title {
            @include text-lg;

            @media screen and (min-width: $breakpoint-md) {
              @include text-xl;

              gap: 12px;
            }
          }
        }
      }
    }

    .right {
      background-image: url("@/assets/confirm/bg-gradient-multi.avif");
      background-size: 1600px;
      background-repeat: no-repeat;
      background-position: center center;
      min-height: auto;
      flex: 1;
      padding-bottom: 60px; // Reduced padding since footer truly overlays

      @media screen and (min-width: $breakpoint-lg) {
        display: block;
        width: 50%;
        flex: 0 0 50%;
        height: unset;
        max-height: unset;
        overflow-y: visible;
        padding-bottom: 0; // Remove bottom padding on desktop
      }

      .linda-testimonial {
        @include flex(column, end, center);

        min-height: 50vh;
        background-image: radial-gradient(#6b4f9730, rgb(97 68 148 / 71%)), var(--bg-img);
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        padding: 30px;
        color: $white;
        text-align: center;

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 60vh;
        }

        @media screen and (min-width: $breakpoint-lg) {
          height: 100%;
          min-height: unset;
          margin-top: 0;
        }

        & > div {
          max-width: 300px;
        }

        .rating {
          @include text-sm;

          color: $bilbao500;

          img {
            vertical-align: text-bottom;
          }
        }
      }

      .calendar-appointment {
        @include flex(column, center, center);

        min-height: 30vh;
        max-height: 40vh;
        margin-top: 20px; // Add top margin to align with left content
        padding: 15px;
        overflow: hidden;

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 35vh;
          max-height: 45vh;
          margin-top: 25px; // Match left padding
          padding: 20px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          height: 100%;
          min-height: unset;
          max-height: unset;
          margin-top: 0;
          padding: 0;
          overflow: visible;
          justify-content: flex-start;
        }

        .appointment {
          width: 100%;
          max-width: 100%;
          height: 100%;
          max-height: 100%;
          object-fit: contain;
          object-position: center top;

          @media screen and (min-width: $breakpoint-lg) {
            width: 100%;
            height: 100vh;
            object-fit: cover;
            object-position: center top;
          }
        }
      }

      .what-you-get-scroll {
        @include flex(column, start, center);

        background-image: linear-gradient(to bottom, #ffffff7d 0%, #fff0 10%, #fff0 90%, #ffffff7d 100%);
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
        overflow: hidden;
        min-height: 50vh;
        max-height: 50vh;
        padding: 0 0 20px;

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 60vh;
          max-height: 80vh;
        }

        @media screen and (min-width: $breakpoint-lg) {
          height: 100%;
          min-height: unset;
          max-height: unset;
          padding: 0;
          margin-top: 0;
        }

        .wyg {
          animation: scroll 10s linear infinite;
          height: 150%;
          width: auto;
          object-fit: contain;

          @media screen and (min-width: $breakpoint-lg) {
            width: 400px;
            object-fit: cover;
            object-position: center;
          }
        }

        @keyframes scroll {
          0% {
            transform: translateY(0%);
          }

          100% {
            transform: translateY(-33.2%);
          }
        }
      }

      .how-it-works-steps {
        @include flex(column, center, center, $gap: 18px);
        @include text-sm;

        min-height: 50vh;
        padding: 30px 10px;
        margin-top: 20px; // Add top margin to align with left content

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 60vh;
          padding: 40px 15px;
          margin-top: 25px; // Match left padding
        }

        @media screen and (min-width: $breakpoint-lg) {
          @include text-md;

          height: 100%;
          min-height: unset;
          padding: unset;
          gap: 35px;
          margin-top: 0;
        }
      }

      .time-warning-section {
        @include flex(column, center, center, $gap: 10px);
        @include text-md;

        background: linear-gradient(to bottom, #dcdcdcb9 0%, #fff0 30%, #fff0 70%, #dcdcdcb9 100%);
        background-size: auto;
        background-repeat: no-repeat;
        background-position: center center;
        padding: 30px 15px;
        color: $white;
        text-align: center;
        min-height: 50vh;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-lg;

          padding: 40px 30px;
          min-height: 60vh;
        }

        @media screen and (min-width: $breakpoint-lg) {
          background:
            linear-gradient(to bottom, #dcdcdcb9 0%, #fff0 30%, #fff0 70%, #dcdcdcb9 100%),
            url("@/assets/confirm/job-boards.avif");
          background-size: auto, 575px;
          height: 100%;
          min-height: unset;
          padding: 15px;
          margin-top: 0;
        }

        .time {
          @include flex(column, center, center, $gap: 8px);
        }
      }

      .testimonials-list {
        @include flex(column, center, stretch, $gap: 25px);

        min-height: 70vh;
        overflow: hidden;
        text-align: center;
        padding: 20px;

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 80vh;
          padding: 30px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          height: 100%;
          min-height: unset;
        }
      }

      .see-you-there-card {
        @include flex(column, center, center, $gap: 35px);

        min-height: 60vh;
        overflow: hidden;
        padding: 20px 0;

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 70vh;
          padding: 30px;
        }

        @media screen and (min-width: $breakpoint-lg) {
          height: 100%;
          min-height: unset;
          padding: unset;
        }

        .testimonial {
          width: 90%;

          @media screen and (min-width: $breakpoint-sm) {
            width: 80%;
          }

          @media screen and (min-width: $breakpoint-md) {
            width: 70%;
          }

          @media screen and (min-width: $breakpoint-xl) {
            width: 60%;
          }
        }

        .schools {
          @include grid($cols: repeat(3, 1fr), $rows: auto, $align-items: center);

          justify-items: center;

          @media screen and (min-width: $breakpoint-xs) {
            grid-template-columns: repeat(4, 1fr);
          }

          @media screen and (min-width: $breakpoint-sm) {
            grid-template-columns: repeat(6, 1fr);
          }

          .school {
            width: 100px;
          }
        }
      }

      .testimonials-stats {
        @include flex(column, center, center, $gap: 35px);

        min-height: 70vh;
        overflow: hidden;
        text-align: center;
        padding: 30px 0;
        margin-top: 20px; // Add top margin to align with left content

        @media screen and (min-width: $breakpoint-sm) {
          min-height: 80vh;
          margin-top: 25px; // Match left padding
        }

        @media screen and (min-width: $breakpoint-lg) {
          height: 100%;
          min-height: unset;
          padding: unset;
          gap: 40px;
          margin-top: 0;
        }

        .reviews {
          @include flex(row, start, stretch, $gap: 16px);

          animation: scrollx 45s linear infinite;
          text-align: start;

          .review {
            @include flex(column, space-between, stretch, $gap: 24px);
            @include border-radius-md;
            @include text-sm;

            background-color: $white;
            padding: 24px;
            width: 320px;
            box-shadow: 0 14px 18px 0 #122b6905;

            @media screen and (min-width: $breakpoint-md) {
              width: 420px;
            }

            .top {
              @include flex(column, start, stretch, $gap: 24px);

              .rating {
                > img {
                  vertical-align: sub;
                  width: 90px;

                  @media screen and (min-width: $breakpoint-md) {
                    width: unset;
                  }
                }
              }
            }

            .person {
              .name {
                font-weight: 700;
              }
            }
          }
        }

        .stats {
          @include grid($cols: repeat(3, 1fr), $rows: auto, $align-items: stretch);

          text-align: center;

          @media screen and (min-width: $breakpoint-lg) {
            text-align: start;
          }

          .stat {
            @include flex(column, center, center);
            @include text-sm;

            padding: 0 10px;

            @media screen and (min-width: $breakpoint-md) {
              padding: 0 20px;
            }

            @media screen and (min-width: $breakpoint-lg) {
              align-items: center;
            }

            &:not(:last-child) {
              border-right: 1px solid $black300;
            }

            @media screen and (min-width: $breakpoint-xl) {
              padding: 0 30px;
            }
          }
        }
      }
    }
  }

  @keyframes scrollx {
    0% {
      transform: translateX(10%);
    }

    100% {
      transform: translateX(-40%);
    }
  }

  .actions {
    @include flex(row, space-between, stretch);

    padding: 15px 20px;
    flex-shrink: 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: $white;
    border-top: 1px solid #e5e7eb;
    z-index: 100;
    pointer-events: auto; // Ensure buttons are clickable

    @media screen and (min-width: $breakpoint-lg) {
      position: relative;
      padding: unset;
      background-color: transparent;
      border-top: none;
      z-index: auto;
      pointer-events: auto;
    }
  }
}
</style>

<style lang="scss">
.i-confirmation {
  .highlighty {
    background-color: #e2f7f0;
  }
}
</style>
